/**
 * Real-Time Provider & Hook — Platform Event Bus Frontend
 * 
 * Provides a single WebSocket connection per session with:
 * - Auto-reconnect with exponential backoff
 * - Event subscription by type or category
 * - Toast notifications for critical events
 * - Connection state management
 * 
 * Philosophy: Every important workflow event flows through this system.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

// Event categories
const EVENT_CATEGORIES = {
  COMMUNICATION: 'communication',
  VISIT: 'visit',
  ASSIGNMENT: 'assignment',
  SCHEDULE: 'schedule',
  CARE: 'care',
  APPROVAL: 'approval',
  ALERT: 'alert',
  PAYROLL: 'payroll',
  BILLING: 'billing',
  SYSTEM: 'system',
};

// Event priorities
const EVENT_PRIORITIES = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  CRITICAL: 'critical',
};

// Connection states
const CONNECTION_STATES = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
};

// Context
const RealtimeContext = createContext(null);

/**
 * RealtimeProvider — Manages WebSocket connection and event distribution
 */
export function RealtimeProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 1000;
  
  // State
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
  const [lastEvent, setLastEvent] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  
  // Event listeners registry
  const listenersRef = useRef(new Map()); // Map<listenerId, {callback, eventTypes, categories}>
  
  // Get WebSocket URL from the API URL
  const getWsUrl = useCallback(() => {
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Convert http(s) to ws(s)
    const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsHost = apiUrl.replace(/^https?:\/\//, '').replace(/\/api$/, '');
    return `${wsProtocol}://${wsHost}/api/ws/connect`;
  }, []);
  
  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!isAuthenticated || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }
    
    setConnectionState(CONNECTION_STATES.CONNECTING);
    
    try {
      const wsUrl = getWsUrl();
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('[Realtime] Connected');
        setConnectionState(CONNECTION_STATES.CONNECTED);
        reconnectAttempts.current = 0;
        
        // Request missed events since last connection
        const lastEventTime = localStorage.getItem('lastEventTime');
        if (lastEventTime) {
          ws.send(JSON.stringify({
            type: 'get_history',
            payload: { since: lastEventTime, limit: 50 },
          }));
        }
      };
      
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleMessage(message);
        } catch (err) {
          console.error('[Realtime] Failed to parse message:', err);
        }
      };
      
      ws.onclose = (event) => {
        console.log('[Realtime] Disconnected:', event.code, event.reason);
        setConnectionState(CONNECTION_STATES.DISCONNECTED);
        wsRef.current = null;
        
        // Attempt reconnection if not a clean close
        if (event.code !== 1000 && isAuthenticated) {
          scheduleReconnect();
        }
      };
      
      ws.onerror = (error) => {
        console.error('[Realtime] WebSocket error:', error);
      };
      
    } catch (err) {
      console.error('[Realtime] Failed to connect:', err);
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
      scheduleReconnect();
    }
  }, [isAuthenticated, getWsUrl]);
  
  // Schedule reconnection with exponential backoff
  const scheduleReconnect = useCallback(() => {
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      console.error('[Realtime] Max reconnection attempts reached');
      return;
    }
    
    const delay = Math.min(
      baseReconnectDelay * Math.pow(2, reconnectAttempts.current),
      30000 // Max 30 seconds
    );
    
    console.log(`[Realtime] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1})`);
    setConnectionState(CONNECTION_STATES.RECONNECTING);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttempts.current++;
      connect();
    }, delay);
  }, [connect]);
  
  // Disconnect
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    
    setConnectionState(CONNECTION_STATES.DISCONNECTED);
  }, []);
  
  // Handle incoming messages
  const handleMessage = useCallback((message) => {
    const { type, payload } = message;
    
    switch (type) {
      case 'connected':
        console.log('[Realtime] Session established:', payload);
        break;
        
      case 'event':
        handleEvent(payload);
        break;
        
      case 'history':
        // Process historical events
        payload.events?.forEach(event => handleEvent(event, true));
        break;
        
      case 'pong':
        // Heartbeat response
        break;
        
      case 'subscribed':
      case 'unsubscribed':
        console.log(`[Realtime] ${type}:`, payload);
        break;
        
      case 'error':
        console.error('[Realtime] Server error:', payload);
        break;
        
      default:
        console.log('[Realtime] Unknown message type:', type, payload);
    }
  }, []);
  
  // Handle a single event
  const handleEvent = useCallback((event, isHistorical = false) => {
    setLastEvent(event);
    
    // Store last event time for reconnection
    if (event.created_at) {
      localStorage.setItem('lastEventTime', event.created_at);
    }
    
    // Notify all matching listeners
    listenersRef.current.forEach((listener) => {
      const { callback, eventTypes, categories } = listener;
      
      // Check if listener wants this event
      let shouldNotify = false;
      
      if (!eventTypes?.length && !categories?.length) {
        // No filters = receive all events
        shouldNotify = true;
      } else {
        if (eventTypes?.includes(event.event_type)) {
          shouldNotify = true;
        }
        if (categories?.includes(event.category)) {
          shouldNotify = true;
        }
      }
      
      if (shouldNotify) {
        try {
          callback(event, isHistorical);
        } catch (err) {
          console.error('[Realtime] Listener error:', err);
        }
      }
    });
    
    // Show toast for high/critical priority events (if not historical)
    if (!isHistorical && (event.priority === EVENT_PRIORITIES.HIGH || event.priority === EVENT_PRIORITIES.CRITICAL)) {
      showEventToast(event);
    }
  }, []);
  
  // Show toast notification for important events
  const showEventToast = useCallback((event) => {
    const toastOptions = {
      description: event.message,
      duration: event.priority === EVENT_PRIORITIES.CRITICAL ? 10000 : 5000,
    };
    
    if (event.priority === EVENT_PRIORITIES.CRITICAL) {
      toast.error(event.title, toastOptions);
    } else if (event.category === EVENT_CATEGORIES.ALERT) {
      toast.warning(event.title, toastOptions);
    } else {
      toast.info(event.title, toastOptions);
    }
  }, []);
  
  // Send a message through WebSocket
  const send = useCallback((type, payload = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, payload }));
      return true;
    }
    return false;
  }, []);
  
  // Subscribe to events (used internally by useRealtime hook)
  const subscribe = useCallback((listenerId, callback, options = {}) => {
    const { eventTypes = [], categories = [] } = options;
    
    listenersRef.current.set(listenerId, {
      callback,
      eventTypes,
      categories,
    });
    
    // Tell server about subscriptions
    if (eventTypes.length || categories.length) {
      send('subscribe', { event_types: eventTypes, categories });
    }
    
    return () => {
      listenersRef.current.delete(listenerId);
    };
  }, [send]);
  
  // Ping for keepalive
  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        send('ping', { timestamp: new Date().toISOString() });
      }
    }, 25000); // Ping every 25 seconds
    
    return () => clearInterval(pingInterval);
  }, [send]);
  
  // Connect/disconnect based on auth state
  useEffect(() => {
    if (isAuthenticated) {
      connect();
    } else {
      disconnect();
    }
    
    return () => disconnect();
  }, [isAuthenticated, connect, disconnect]);
  
  // Context value
  const value = {
    connectionState,
    isConnected: connectionState === CONNECTION_STATES.CONNECTED,
    lastEvent,
    onlineUsers,
    subscribe,
    send,
    reconnect: connect,
    EVENT_CATEGORIES,
    EVENT_PRIORITIES,
    CONNECTION_STATES,
  };
  
  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
}

/**
 * useRealtime — Hook for subscribing to real-time events
 * 
 * @param {Object} options
 * @param {string[]} options.eventTypes - Specific event types to listen for
 * @param {string[]} options.categories - Event categories to listen for
 * @param {Function} options.onEvent - Callback when event is received
 * 
 * @returns {Object} - { isConnected, lastEvent, send }
 */
export function useRealtime(options = {}) {
  const context = useContext(RealtimeContext);
  
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  
  const { eventTypes = [], categories = [], onEvent } = options;
  const [events, setEvents] = useState([]);
  const listenerIdRef = useRef(Math.random().toString(36).substr(2, 9));
  
  // Subscribe to events
  useEffect(() => {
    if (!onEvent && eventTypes.length === 0 && categories.length === 0) {
      return; // No subscription needed if no filters and no callback
    }
    
    const handleEvent = (event, isHistorical) => {
      setEvents(prev => [event, ...prev.slice(0, 99)]); // Keep last 100 events
      
      if (onEvent) {
        onEvent(event, isHistorical);
      }
    };
    
    const unsubscribe = context.subscribe(
      listenerIdRef.current,
      handleEvent,
      { eventTypes, categories }
    );
    
    return unsubscribe;
  }, [context, eventTypes.join(','), categories.join(','), onEvent]);
  
  return {
    isConnected: context.isConnected,
    connectionState: context.connectionState,
    lastEvent: context.lastEvent,
    events,
    send: context.send,
    reconnect: context.reconnect,
    EVENT_CATEGORIES: context.EVENT_CATEGORIES,
    EVENT_PRIORITIES: context.EVENT_PRIORITIES,
  };
}

/**
 * useConnectionStatus — Simple hook for connection indicator
 */
export function useConnectionStatus() {
  const context = useContext(RealtimeContext);
  
  if (!context) {
    return { isConnected: false, connectionState: CONNECTION_STATES.DISCONNECTED };
  }
  
  return {
    isConnected: context.isConnected,
    connectionState: context.connectionState,
  };
}

export { EVENT_CATEGORIES, EVENT_PRIORITIES, CONNECTION_STATES };
export default RealtimeContext;
